import { useState, useEffect, useRef } from 'react';
import { Plus, Play, Pause, RotateCcw, Trash2, X, Clock, MessageSquare, Save, FolderOpen, History, CheckCircle2 } from 'lucide-react';
import { useSafeThemeClasses } from '@/shared/theme';
import { storageUtils } from '@/shared/utils';
import { logger } from '@/shared/utils';
import { useConfirmDialog } from '@/shared/components/ui';

// Storage key for meetings
const STORAGE_KEY = 'toastmasters_meetings';

// Types
interface TimerSpeaker {
  id: string;
  name: string;
  role: string;
  times: TimerPreset;
  elapsed: number;
  isRunning: boolean;
  startTime?: number;
}

interface TimerPreset {
  green: number;
  yellow: number;
  red: number;
  dq: number;
}

interface GrammarianEntry {
  id: string;
  speakerName: string;
  role: string;
  fillerWords: Record<string, number>;
  wordOfDayCount: number;
  notes: string;
  timestamp: number;
}

interface AhCounterEntry {
  id: string;
  speakerName: string;
  fillerWord: string;
  timestamp: number;
}

interface Meeting {
  id: string;
  name: string;
  date: string;
  wordOfDay: string;
  timerSpeakers: TimerSpeaker[];
  grammarianEntries: GrammarianEntry[];
  ahCounterEntries: AhCounterEntry[];
  meetingNotes: string;
  createdAt: number;
  updatedAt: number;
}

interface ChangeHistory {
  id: string;
  type: 'timer' | 'grammarian' | 'ahcounter' | 'meeting';
  action: string;
  timestamp: number;
  details: string;
}

// Timer Presets for different roles
const ROLE_PRESETS: Record<string, TimerPreset> = {
  'Table Topics': { green: 60, yellow: 90, red: 120, dq: 150 },
  'Icebreaker (4-6 min)': { green: 240, yellow: 300, red: 360, dq: 390 },
  'Speech (5-7 min)': { green: 300, yellow: 360, red: 420, dq: 450 },
  'Speech (8-10 min)': { green: 480, yellow: 540, red: 600, dq: 630 },
  'Evaluation (2-3 min)': { green: 120, yellow: 150, red: 180, dq: 210 },
  'Toastmaster': { green: 180, yellow: 240, red: 300, dq: 330 },
  'General Evaluator': { green: 180, yellow: 240, red: 300, dq: 330 },
  'Grammarian': { green: 180, yellow: 240, red: 300, dq: 330 },
  'Ah-Counter': { green: 180, yellow: 240, red: 300, dq: 330 },
  'Custom': { green: 60, yellow: 90, red: 120, dq: 150 }
};

const FILLER_WORDS = ['um', 'uh', 'ah', 'like', 'you know', 'so', 'actually', 'basically', 'literally', 'well', 'er', 'hmm'];

export default function ToastmastersSuite() {
  const [activeTab, setActiveTab] = useState<'timer' | 'grammarian' | 'ahcounter' | 'meetings' | 'history'>('timer');
  const [currentMeeting, setCurrentMeeting] = useState<Meeting | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [changeHistory, setChangeHistory] = useState<ChangeHistory[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const theme = useSafeThemeClasses();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Load meetings from storage on mount
  useEffect(() => {
    loadMeetings();
  }, []);

  // Auto-save current meeting every 30 seconds
  useEffect(() => {
    if (!currentMeeting) return;
    
    const interval = setInterval(() => {
      saveCurrentMeeting();
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(interval);
  }, [currentMeeting]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMeetings = () => {
    try {
      const stored = storageUtils.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setMeetings(parsed);
        logger.info('Loaded meetings from storage', 'ToastmastersSuite', { count: parsed.length });
      }
    } catch (error) {
      logger.error('Failed to load meetings', 'ToastmastersSuite', { error });
    }
  };

  const saveMeetings = (meetingsToSave: Meeting[]) => {
    try {
      storageUtils.setItem(STORAGE_KEY, JSON.stringify(meetingsToSave));
      setMeetings(meetingsToSave);
      logger.info('Saved meetings to storage', 'ToastmastersSuite', { count: meetingsToSave.length });
    } catch (error) {
      logger.error('Failed to save meetings', 'ToastmastersSuite', { error });
    }
  };

  const createNewMeeting = (meetingName: string, meetingDate?: string) => {
    if (!meetingName.trim()) return;

    const newMeeting: Meeting = {
      id: `meeting_${Date.now()}`,
      name: meetingName.trim(),
      date: meetingDate || new Date().toISOString().split('T')[0],
      wordOfDay: '',
      timerSpeakers: [],
      grammarianEntries: [],
      ahCounterEntries: [],
      meetingNotes: '',
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    // Immediately save to meetings list
    const updatedMeetings = [...meetings, newMeeting];
    saveMeetings(updatedMeetings);
    
    setCurrentMeeting(newMeeting);
    setShowCreateModal(false);
    addToHistory('meeting', 'Created new meeting', `Meeting: ${meetingName}`);
    logger.info('Created new meeting', 'ToastmastersSuite', { meetingId: newMeeting.id });
  };

  const loadMeeting = (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (meeting) {
      setCurrentMeeting(meeting);
      addToHistory('meeting', 'Loaded meeting', `Meeting: ${meeting.name}`);
      logger.info('Loaded meeting', 'ToastmastersSuite', { meetingId });
    }
  };

  const saveCurrentMeeting = () => {
    if (!currentMeeting) return;

    const updated = {
      ...currentMeeting,
      updatedAt: Date.now()
    };

    const existingIndex = meetings.findIndex(m => m.id === currentMeeting.id);
    let updatedMeetings: Meeting[];

    if (existingIndex >= 0) {
      updatedMeetings = [...meetings];
      updatedMeetings[existingIndex] = updated;
    } else {
      updatedMeetings = [...meetings, updated];
    }

    saveMeetings(updatedMeetings);
    setCurrentMeeting(updated);
    addToHistory('meeting', 'Auto-saved meeting', `Meeting: ${updated.name}`);
  };

  const deleteMeeting = async (meetingId: string) => {
    const ok = await confirm({ title: 'Delete this meeting?', message: 'This action cannot be undone.', confirmLabel: 'Delete' });
    if (!ok) return;

    const updatedMeetings = meetings.filter(m => m.id !== meetingId);
    saveMeetings(updatedMeetings);
    
    if (currentMeeting?.id === meetingId) {
      setCurrentMeeting(null);
    }
    
    addToHistory('meeting', 'Deleted meeting', `Meeting ID: ${meetingId}`);
    logger.info('Deleted meeting', 'ToastmastersSuite', { meetingId });
  };

  const addToHistory = (type: ChangeHistory['type'], action: string, details: string) => {
    const historyEntry: ChangeHistory = {
      id: `history_${Date.now()}`,
      type,
      action,
      timestamp: Date.now(),
      details
    };
    setChangeHistory(prev => [historyEntry, ...prev].slice(0, 100)); // Keep last 100 entries
  };

  const exportMeeting = (meeting: Meeting) => {
    const report = generateMeetingReport(meeting);
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `toastmasters-${meeting.name}-${meeting.date}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateMeetingReport = (meeting: Meeting): string => {
    let report = `TOASTMASTERS MEETING REPORT\n`;
    report += `Meeting: ${meeting.name}\n`;
    report += `Date: ${meeting.date}\n`;
    report += `Word of the Day: ${meeting.wordOfDay || 'Not specified'}\n`;
    report += `\n${'='.repeat(60)}\n\n`;

    // Timer Section
    if (meeting.timerSpeakers.length > 0) {
      report += `TIMER RESULTS\n`;
      report += `${'-'.repeat(60)}\n`;
      meeting.timerSpeakers.forEach(speaker => {
        const mins = Math.floor(speaker.elapsed / 60);
        const secs = speaker.elapsed % 60;
        report += `${speaker.name} (${speaker.role}): ${mins}:${secs.toString().padStart(2, '0')}\n`;
      });
      report += `\n`;
    }

    // Grammarian Section
    if (meeting.grammarianEntries.length > 0) {
      report += `GRAMMARIAN REPORT\n`;
      report += `${'-'.repeat(60)}\n`;
      meeting.grammarianEntries.forEach(entry => {
        report += `\n${entry.speakerName} - ${entry.role}\n`;
        const totalFillers = Object.values(entry.fillerWords).reduce((a, b) => a + b, 0);
        report += `Total Filler Words: ${totalFillers}\n`;
        Object.entries(entry.fillerWords).forEach(([word, count]) => {
          if (count > 0) report += `  ${word}: ${count}\n`;
        });
        report += `Word of Day Usage: ${entry.wordOfDayCount}\n`;
        if (entry.notes) report += `Notes: ${entry.notes}\n`;
      });
      report += `\n`;
    }

    // Ah-Counter Section
    if (meeting.ahCounterEntries.length > 0) {
      report += `AH-COUNTER REPORT\n`;
      report += `${'-'.repeat(60)}\n`;
      const bySpeaker: Record<string, Record<string, number>> = {};
      meeting.ahCounterEntries.forEach(entry => {
        if (!bySpeaker[entry.speakerName]) {
          bySpeaker[entry.speakerName] = {};
        }
        bySpeaker[entry.speakerName][entry.fillerWord] = (bySpeaker[entry.speakerName][entry.fillerWord] || 0) + 1;
      });

      Object.entries(bySpeaker).forEach(([speaker, words]) => {
        report += `\n${speaker}:\n`;
        const total = Object.values(words).reduce((a, b) => a + b, 0);
        report += `  Total: ${total}\n`;
        Object.entries(words).forEach(([word, count]) => {
          report += `  ${word}: ${count}\n`;
        });
      });
      report += `\n`;
    }

    if (meeting.meetingNotes) {
      report += `MEETING NOTES\n`;
      report += `${'-'.repeat(60)}\n`;
      report += `${meeting.meetingNotes}\n`;
    }

    return report;
  };

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <ConfirmDialog />
      <div className={`${theme.header} border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className={`text-3xl font-bold ${theme.textHeading}`}>Toastmasters Suite</h1>
            <div className="flex gap-2">
              {currentMeeting ? (
                <>
                  <button
                    onClick={saveCurrentMeeting}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
                  >
                    <Save size={18} />
                    Save
                  </button>
                  <span className={`px-4 py-2 ${theme.textSecondary}`}>
                    {currentMeeting.name}
                  </span>
                </>
              ) : (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
                >
                  <Plus size={18} />
                  New Meeting
                </button>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveTab('timer')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === 'timer' 
                  ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                  : `${theme.buttonSecondary} ${theme.textSecondary}`
              }`}
            >
              <Clock size={18} />
              Timer
            </button>
            <button
              onClick={() => setActiveTab('grammarian')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === 'grammarian' 
                  ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                  : `${theme.buttonSecondary} ${theme.textSecondary}`
              }`}
            >
              <MessageSquare size={18} />
              Grammarian
            </button>
            <button
              onClick={() => setActiveTab('ahcounter')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === 'ahcounter' 
                  ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                  : `${theme.buttonSecondary} ${theme.textSecondary}`
              }`}
            >
              <CheckCircle2 size={18} />
              Ah-Counter
            </button>
            <button
              onClick={() => setActiveTab('meetings')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === 'meetings' 
                  ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                  : `${theme.buttonSecondary} ${theme.textSecondary}`
              }`}
            >
              <FolderOpen size={18} />
              Meetings
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === 'history' 
                  ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                  : `${theme.buttonSecondary} ${theme.textSecondary}`
              }`}
            >
              <History size={18} />
              History
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {!currentMeeting && activeTab !== 'meetings' && activeTab !== 'history' && (
          <div className={`${theme.card} p-8 text-center rounded-lg`}>
            <p className={`${theme.textSecondary} mb-4`}>No meeting active. Create or load a meeting to start tracking.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className={`px-6 py-3 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
            >
              Create New Meeting
            </button>
          </div>
        )}

        {activeTab === 'timer' && currentMeeting && (
          <TimerTool 
            meeting={currentMeeting}
            onUpdate={(updated) => {
              setCurrentMeeting(updated);
              addToHistory('timer', 'Updated timer', 'Timer data changed');
            }}
          />
        )}

        {activeTab === 'grammarian' && currentMeeting && (
          <GrammarianTool 
            meeting={currentMeeting}
            onUpdate={(updated) => {
              setCurrentMeeting(updated);
              addToHistory('grammarian', 'Updated grammarian', 'Grammarian data changed');
            }}
          />
        )}

        {activeTab === 'ahcounter' && currentMeeting && (
          <AhCounterTool 
            meeting={currentMeeting}
            onUpdate={(updated) => {
              setCurrentMeeting(updated);
              addToHistory('ahcounter', 'Updated ah-counter', 'Ah-counter data changed');
            }}
          />
        )}

        {activeTab === 'meetings' && (
          <MeetingsManager 
            meetings={meetings}
            currentMeeting={currentMeeting}
            onLoad={loadMeeting}
            onDelete={deleteMeeting}
            onExport={exportMeeting}
            onCreate={() => setShowCreateModal(true)}
          />
        )}

        {activeTab === 'history' && (
          <HistoryView history={changeHistory} />
        )}

        {showCreateModal && (
          <CreateMeetingModal
            onCreate={createNewMeeting}
            onClose={() => setShowCreateModal(false)}
          />
        )}
      </div>
    </div>
  );
}

// TIMER TOOL
function TimerTool({ meeting, onUpdate }: { meeting: Meeting; onUpdate: (meeting: Meeting) => void }) {
  const theme = useSafeThemeClasses();
  const [speakers, setSpeakers] = useState<TimerSpeaker[]>(meeting.timerSpeakers);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setSpeakers(meeting.timerSpeakers);
  }, [meeting.id, meeting.timerSpeakers]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSpeakers(prev => prev.map(s => {
        if (s.isRunning && s.startTime) {
          const elapsed = Math.floor((Date.now() - s.startTime) / 1000);
          return { ...s, elapsed };
        }
        return s;
      }));
    }, 100);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    onUpdate({ ...meeting, timerSpeakers: speakers });
  }, [speakers]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSpeaker = (name: string, role: string, times: TimerPreset) => {
    const newSpeaker: TimerSpeaker = {
      id: `timer_${Date.now()}`,
      name,
      role,
      times,
      elapsed: 0,
      isRunning: false
    };
    setSpeakers([...speakers, newSpeaker]);
  };

  const startTimer = (id: string) => {
    if (activeTimer && activeTimer !== id) {
      setSpeakers(prev => prev.map(s => 
        s.id === activeTimer ? { ...s, isRunning: false, startTime: undefined } : s
      ));
    }
    setSpeakers(prev => prev.map(s => 
      s.id === id ? { ...s, isRunning: true, startTime: Date.now() } : s
    ));
    setActiveTimer(id);
  };

  const pauseTimer = (id: string) => {
    setSpeakers(prev => prev.map(s => 
      s.id === id ? { ...s, isRunning: false, startTime: undefined } : s
    ));
  };

  const resetTimer = (id: string) => {
    setSpeakers(prev => prev.map(s => 
      s.id === id ? { ...s, elapsed: 0, isRunning: false, startTime: undefined } : s
    ));
  };

  const deleteSpeaker = (id: string) => {
    setSpeakers(prev => prev.filter(s => s.id !== id));
    if (activeTimer === id) setActiveTimer(null);
  };

  const getTimerColor = (elapsed: number, times: TimerPreset, theme: ReturnType<typeof useSafeThemeClasses>): string => {
    // Timer status colors need to be visible, but use theme text where possible
    if (elapsed >= times.dq) return 'bg-black text-white';
    if (elapsed >= times.red) return 'bg-red-600 text-white';
    if (elapsed >= times.yellow) return 'bg-yellow-400 text-black';
    if (elapsed >= times.green) return 'bg-green-500 text-white';
    return `${theme.card} ${theme.textPrimary}`;
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const activeSpeaker = speakers.find(s => s.id === activeTimer);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Meeting Timer</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
        >
          <Plus size={20} />
          Add Speaker
        </button>
      </div>

      {activeSpeaker && (
        <div className={`mb-8 p-8 rounded-2xl transition-colors duration-500 ${getTimerColor(activeSpeaker.elapsed, activeSpeaker.times, theme)}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-semibold mb-2 ${activeSpeaker.elapsed >= activeSpeaker.times.dq || activeSpeaker.elapsed >= activeSpeaker.times.red || activeSpeaker.elapsed >= activeSpeaker.times.green ? 'text-white' : activeSpeaker.elapsed >= activeSpeaker.times.yellow ? 'text-black' : theme.textHeading}`}>{activeSpeaker.name}</h2>
            <p className={`text-lg mb-4 opacity-80 ${activeSpeaker.elapsed >= activeSpeaker.times.dq || activeSpeaker.elapsed >= activeSpeaker.times.red || activeSpeaker.elapsed >= activeSpeaker.times.green ? 'text-white' : activeSpeaker.elapsed >= activeSpeaker.times.yellow ? 'text-black' : theme.textSecondary}`}>{activeSpeaker.role}</p>
            <div className={`text-7xl font-bold mb-6 ${activeSpeaker.elapsed >= activeSpeaker.times.dq || activeSpeaker.elapsed >= activeSpeaker.times.red || activeSpeaker.elapsed >= activeSpeaker.times.green ? 'text-white' : activeSpeaker.elapsed >= activeSpeaker.times.yellow ? 'text-black' : theme.textHeading}`}>{formatTime(activeSpeaker.elapsed)}</div>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => activeSpeaker.isRunning ? pauseTimer(activeSpeaker.id) : startTimer(activeSpeaker.id)}
                className={`p-4 rounded-full transition ${theme.buttonSecondary} ${theme.textSecondary} bg-opacity-20 hover:bg-opacity-30`}
              >
                {activeSpeaker.isRunning ? <Pause size={32} /> : <Play size={32} />}
              </button>
              <button
                onClick={() => resetTimer(activeSpeaker.id)}
                className={`p-4 rounded-full transition ${theme.buttonSecondary} ${theme.textSecondary} bg-opacity-20 hover:bg-opacity-30`}
              >
                <RotateCcw size={32} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {speakers.map(speaker => (
          <div
            key={speaker.id}
            className={`${theme.card} rounded-lg p-4 border-2 transition ${
              activeTimer === speaker.id ? 'border-blue-500' : 'border-transparent'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className={`font-semibold text-lg ${theme.textHeading}`}>{speaker.name}</h3>
                <p className={`${theme.textSecondary} text-sm`}>{speaker.role}</p>
              </div>
              
              <div className={`px-6 py-3 rounded-lg font-mono text-2xl font-bold ${getTimerColor(speaker.elapsed, speaker.times, theme)}`}>
                {formatTime(speaker.elapsed)}
              </div>

              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => speaker.isRunning ? pauseTimer(speaker.id) : startTimer(speaker.id)}
                  className={`p-2 rounded transition ${theme.buttonSecondary}`}
                >
                  {speaker.isRunning ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <button
                  onClick={() => resetTimer(speaker.id)}
                  className={`p-2 rounded transition ${theme.buttonGhost}`}
                >
                  <RotateCcw size={20} />
                </button>
                <button
                  onClick={() => deleteSpeaker(speaker.id)}
                  className={`p-2 rounded transition ${theme.buttonSecondary}`}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>

            <div className={`mt-3 flex gap-4 text-xs ${theme.textMuted}`}>
              <span>🟢 {formatTime(speaker.times.green)}</span>
              <span>🟡 {formatTime(speaker.times.yellow)}</span>
              <span>🔴 {formatTime(speaker.times.red)}</span>
              <span>⛔ {formatTime(speaker.times.dq)}</span>
            </div>
          </div>
        ))}
      </div>

      {speakers.length === 0 && (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          No speakers added yet. Click "Add Speaker" to start.
        </div>
      )}

      {showAddForm && (
        <AddSpeakerModal
          onAdd={addSpeaker}
          onClose={() => setShowAddForm(false)}
        />
      )}
    </div>
  );
}

function AddSpeakerModal({ onAdd, onClose }: { onAdd: (name: string, role: string, times: TimerPreset) => void; onClose: () => void }) {
  const theme = useSafeThemeClasses();
  const [name, setName] = useState('');
  const [selectedRole, setSelectedRole] = useState('Table Topics');
  const [customTimes, setCustomTimes] = useState<TimerPreset>(ROLE_PRESETS['Table Topics']);

  useEffect(() => {
    setCustomTimes(ROLE_PRESETS[selectedRole]);
  }, [selectedRole]);

  const handleAdd = () => {
    if (!name.trim()) return;
    onAdd(name, selectedRole, customTimes);
    onClose();
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center p-4 z-50 ${theme.modalOverlay}`}>
      <div className={`${theme.card} rounded-xl shadow-xl max-w-md w-full p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Add Speaker</h2>
          <button onClick={onClose} className={`p-2 rounded transition ${theme.buttonGhost}`}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
              className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
              placeholder="Speaker name"
              autoFocus
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${theme.select} ${theme.textPrimary}`}
            >
              {Object.keys(ROLE_PRESETS).map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${theme.textLabel}`}>🟢 Green (sec)</label>
              <input
                type="number"
                value={customTimes.green}
                onChange={(e) => setCustomTimes({...customTimes, green: parseInt(e.target.value) || 0})}
                className={`w-full px-2 py-1 rounded text-sm ${theme.input} ${theme.textPrimary}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${theme.textLabel}`}>🟡 Yellow (sec)</label>
              <input
                type="number"
                value={customTimes.yellow}
                onChange={(e) => setCustomTimes({...customTimes, yellow: parseInt(e.target.value) || 0})}
                className={`w-full px-2 py-1 rounded text-sm ${theme.input} ${theme.textPrimary}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${theme.textLabel}`}>🔴 Red (sec)</label>
              <input
                type="number"
                value={customTimes.red}
                onChange={(e) => setCustomTimes({...customTimes, red: parseInt(e.target.value) || 0})}
                className={`w-full px-2 py-1 rounded text-sm ${theme.input} ${theme.textPrimary}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${theme.textLabel}`}>⛔ DQ (sec)</label>
              <input
                type="number"
                value={customTimes.dq}
                onChange={(e) => setCustomTimes({...customTimes, dq: parseInt(e.target.value) || 0})}
                className={`w-full px-2 py-1 rounded text-sm ${theme.input} ${theme.textPrimary}`}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
            >
              Add Speaker
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// GRAMMARIAN TOOL
function GrammarianTool({ meeting, onUpdate }: { meeting: Meeting; onUpdate: (meeting: Meeting) => void }) {
  const theme = useSafeThemeClasses();
  const [entries, setEntries] = useState<GrammarianEntry[]>(meeting.grammarianEntries);
  const [wordOfDay, setWordOfDay] = useState(meeting.wordOfDay);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    setEntries(meeting.grammarianEntries);
    setWordOfDay(meeting.wordOfDay);
  }, [meeting.id, meeting.grammarianEntries, meeting.wordOfDay]);

  useEffect(() => {
    onUpdate({ ...meeting, grammarianEntries: entries, wordOfDay });
  }, [entries, wordOfDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const addEntry = (speakerName: string, role: string) => {
    const newEntry: GrammarianEntry = {
      id: `grammarian_${Date.now()}`,
      speakerName,
      role,
      fillerWords: {},
      wordOfDayCount: 0,
      notes: '',
      timestamp: Date.now()
    };
    setEntries([...entries, newEntry]);
  };

  const updateFillerWord = (entryId: string, word: string, delta: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === entryId) {
        const current = entry.fillerWords[word] || 0;
        return {
          ...entry,
          fillerWords: {
            ...entry.fillerWords,
            [word]: Math.max(0, current + delta)
          }
        };
      }
      return entry;
    }));
  };

  const updateWordOfDay = (entryId: string, delta: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === entryId) {
        return {
          ...entry,
          wordOfDayCount: Math.max(0, entry.wordOfDayCount + delta)
        };
      }
      return entry;
    }));
  };

  const updateNotes = (entryId: string, notes: string) => {
    setEntries(prev => prev.map(entry => 
      entry.id === entryId ? { ...entry, notes } : entry
    ));
  };

  const deleteEntry = (id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Grammarian Tracker</h2>
        <div className="flex gap-2">
          <div>
            <label className={`block text-xs ${theme.textLabel} mb-1`}>Word of the Day</label>
            <input
              type="text"
              value={wordOfDay}
              onChange={(e) => setWordOfDay(e.target.value)}
              className={`px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
              placeholder="Enter word of the day"
            />
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
          >
            <Plus size={20} />
            Add Speaker
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {entries.map(entry => (
          <div key={entry.id} className={`${theme.card} rounded-lg p-4`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className={`font-semibold text-lg ${theme.textHeading}`}>{entry.speakerName}</h3>
                <p className={`${theme.textSecondary} text-sm`}>{entry.role}</p>
              </div>
              <button
                onClick={() => deleteEntry(entry.id)}
                className={`p-2 rounded transition ${theme.buttonGhost}`}
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textLabel}`}>Filler Words</label>
                <div className="flex flex-wrap gap-2">
                  {FILLER_WORDS.map(word => {
                    const count = entry.fillerWords[word] || 0;
                    return (
                      <div key={word} className="flex items-center gap-2">
                        <span className={`text-sm ${theme.textSecondary}`}>{word}:</span>
                        <button
                          onClick={() => updateFillerWord(entry.id, word, -1)}
                          className={`px-2 py-1 rounded ${theme.buttonSecondary}`}
                        >
                          -
                        </button>
                        <span className={`px-3 py-1 rounded ${theme.card} min-w-[3rem] text-center ${theme.textHeading}`}>
                          {count}
                        </span>
                        <button
                          onClick={() => updateFillerWord(entry.id, word, 1)}
                          className={`px-2 py-1 rounded ${theme.buttonSecondary}`}
                        >
                          +
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textLabel}`}>
                  Word of Day Usage: {wordOfDay || 'Not set'}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateWordOfDay(entry.id, -1)}
                    className={`px-3 py-1 rounded ${theme.buttonSecondary}`}
                  >
                    -
                  </button>
                  <span className={`px-4 py-2 rounded ${theme.card} min-w-[3rem] text-center text-lg font-bold ${theme.textHeading}`}>
                    {entry.wordOfDayCount}
                  </span>
                  <button
                    onClick={() => updateWordOfDay(entry.id, 1)}
                    className={`px-3 py-1 rounded ${theme.buttonSecondary}`}
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textLabel}`}>Notes</label>
                <textarea
                  value={entry.notes}
                  onChange={(e) => updateNotes(entry.id, e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg ${theme.textarea} ${theme.textPrimary}`}
                  placeholder="Add notes about grammar, strong phrases, etc."
                  rows={3}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {entries.length === 0 && (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          No speakers added yet. Click "Add Speaker" to start tracking.
        </div>
      )}

      {showAddForm && (
        <AddGrammarianModal
          onAdd={addEntry}
          onClose={() => setShowAddForm(false)}
        />
      )}
    </div>
  );
}

function AddGrammarianModal({ onAdd, onClose }: { onAdd: (name: string, role: string) => void; onClose: () => void }) {
  const theme = useSafeThemeClasses();
  const [name, setName] = useState('');
  const [role, setRole] = useState('Prepared Speech (5-7m)');

  const handleAdd = () => {
    if (!name.trim()) return;
    onAdd(name.trim(), role);
    onClose();
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center p-4 z-50 ${theme.modalOverlay}`}>
      <div className={`${theme.card} rounded-xl shadow-xl max-w-md w-full p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Add Speaker</h2>
          <button onClick={onClose} className={`p-2 rounded transition ${theme.buttonGhost}`}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Speaker Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
              className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
              placeholder="Speaker name"
              autoFocus
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${theme.select} ${theme.textPrimary}`}
            >
              {['Prepared Speech (5-7m)', 'Prepared Speech (8-10m)', 'Icebreaker (4-6m)', 'Evaluator (2-3m)', 'Table Topics (1-2m)', 'Toastmaster', 'General Evaluator', 'Grammarian'].map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
            >
              Add Speaker
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// AH-COUNTER TOOL
function AhCounterTool({ meeting, onUpdate }: { meeting: Meeting; onUpdate: (meeting: Meeting) => void }) {
  const theme = useSafeThemeClasses();
  const [entries, setEntries] = useState<AhCounterEntry[]>(meeting.ahCounterEntries);
  const [currentSpeaker, setCurrentSpeaker] = useState('');
  const [speakers, setSpeakers] = useState<string[]>([]);

  useEffect(() => {
    setEntries(meeting.ahCounterEntries);
    const uniqueSpeakers = Array.from(new Set(meeting.ahCounterEntries.map(e => e.speakerName)));
    setSpeakers(uniqueSpeakers);
  }, [meeting.id, meeting.ahCounterEntries]);

  useEffect(() => {
    onUpdate({ ...meeting, ahCounterEntries: entries });
  }, [entries]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFillerWord = (word: string) => {
    if (!currentSpeaker) {
      const name = prompt('Enter speaker name:');
      if (!name) return;
      setCurrentSpeaker(name);
      if (!speakers.includes(name)) {
        setSpeakers([...speakers, name]);
      }
    }

    const newEntry: AhCounterEntry = {
      id: `ah_${Date.now()}_${Math.random()}`,
      speakerName: currentSpeaker,
      fillerWord: word,
      timestamp: Date.now()
    };
    setEntries([...entries, newEntry]);
  };

  const getCounts = (speakerName: string): Record<string, number> => {
    const speakerEntries = entries.filter(e => e.speakerName === speakerName);
    const counts: Record<string, number> = {};
    speakerEntries.forEach(entry => {
      counts[entry.fillerWord] = (counts[entry.fillerWord] || 0) + 1;
    });
    return counts;
  };

  const removeFillerWord = (entryId: string) => {
    setEntries(prev => prev.filter(e => e.id !== entryId));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Ah-Counter Tracker</h2>
        <div>
          <label className={`block text-xs ${theme.textLabel} mb-1`}>Current Speaker</label>
          <input
            type="text"
            value={currentSpeaker}
            onChange={(e) => {
              setCurrentSpeaker(e.target.value);
              if (e.target.value && !speakers.includes(e.target.value)) {
                setSpeakers([...speakers, e.target.value]);
              }
            }}
            className={`px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
            placeholder="Speaker name"
            list="speakers-list"
          />
          <datalist id="speakers-list">
            {speakers.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>
      </div>

      <div className={`${theme.card} p-4 rounded-lg mb-6`}>
        <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>Quick Add Filler Words</h3>
        <div className="flex flex-wrap gap-2">
          {FILLER_WORDS.map(word => (
            <button
              key={word}
              onClick={() => addFillerWord(word)}
              className={`px-4 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4">
        {speakers.map(speaker => {
          const counts = getCounts(speaker);
          const total = Object.values(counts).reduce((a, b) => a + b, 0);
          const speakerEntries = entries.filter(e => e.speakerName === speaker);

          return (
            <div key={speaker} className={`${theme.card} rounded-lg p-4`}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className={`font-semibold text-lg ${theme.textHeading}`}>{speaker}</h3>
                  <p className={`${theme.textSecondary} text-sm`}>Total: {total} filler words</p>
                </div>
              </div>

              <div className="space-y-2">
                {Object.entries(counts).map(([word, count]) => (
                  <div key={word} className="flex items-center justify-between">
                    <span className={`${theme.textSecondary}`}>{word}:</span>
                    <span className={`px-3 py-1 rounded ${theme.card} font-bold ${theme.textHeading}`}>
                      {count}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t">
                <details>
                  <summary className={`cursor-pointer text-sm ${theme.textSecondary}`}>
                    View individual entries ({speakerEntries.length})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {speakerEntries.slice().reverse().map(entry => (
                      <div key={entry.id} className="flex items-center justify-between text-sm">
                        <span className={theme.textSecondary}>
                          {new Date(entry.timestamp).toLocaleTimeString()} - {entry.fillerWord}
                        </span>
                        <button
                          onClick={() => removeFillerWord(entry.id)}
                          className={`p-1 rounded ${theme.buttonGhost}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            </div>
          );
        })}
      </div>

      {speakers.length === 0 && (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          No speakers tracked yet. Select a speaker and click filler words to start tracking.
        </div>
      )}
    </div>
  );
}

// MEETINGS MANAGER
function MeetingsManager({ 
  meetings, 
  currentMeeting, 
  onLoad, 
  onDelete, 
  onExport,
  onCreate 
}: { 
  meetings: Meeting[]; 
  currentMeeting: Meeting | null;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  onExport: (meeting: Meeting) => void;
  onCreate: () => void;
}) {
  const theme = useSafeThemeClasses();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Meetings</h2>
        <button
          onClick={onCreate}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
        >
          <Plus size={20} />
          New Meeting
        </button>
      </div>

      <div className="grid gap-4">
        {meetings.length === 0 ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>
            No meetings saved yet. Create a new meeting to get started.
          </div>
        ) : (
          meetings
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .map(meeting => (
              <div
                key={meeting.id}
                className={`${theme.card} rounded-lg p-4 ${
                  currentMeeting?.id === meeting.id ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className={`font-semibold text-lg ${theme.textHeading}`}>{meeting.name}</h3>
                    <p className={`${theme.textSecondary} text-sm`}>
                      Date: {meeting.date} | 
                      Updated: {new Date(meeting.updatedAt).toLocaleString()}
                    </p>
                    <p className={`${theme.textMuted} text-xs mt-1`}>
                      Speakers: {meeting.timerSpeakers.length} | 
                      Grammarian Entries: {meeting.grammarianEntries.length} | 
                      Ah-Counter Entries: {meeting.ahCounterEntries.length}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {currentMeeting?.id !== meeting.id && (
                      <button
                        onClick={() => onLoad(meeting.id)}
                        className={`px-3 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
                      >
                        Load
                      </button>
                    )}
                    <button
                      onClick={() => onExport(meeting)}
                      className={`px-3 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
                    >
                      Export
                    </button>
                    <button
                      onClick={() => onDelete(meeting.id)}
                      className={`px-3 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
}

// HISTORY VIEW
function HistoryView({ history }: { history: ChangeHistory[] }) {
  const theme = useSafeThemeClasses();

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div>
      <h2 className={`text-2xl font-bold mb-6 ${theme.textHeading}`}>Change History</h2>
      
      {history.length === 0 ? (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          No history yet. Changes will appear here as you track meetings.
        </div>
      ) : (
        <div className="space-y-2">
          {history.map(entry => (
            <div
              key={entry.id}
              className={`${theme.card} rounded-lg p-3 flex justify-between items-center`}
            >
              <div>
                <span className={`font-medium ${theme.textHeading}`}>{entry.action}</span>
                <p className={`text-sm ${theme.textSecondary}`}>{entry.details}</p>
              </div>
              <span className={`text-xs ${theme.textMuted}`}>{formatTime(entry.timestamp)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// CREATE MEETING MODAL
function CreateMeetingModal({ 
  onCreate, 
  onClose 
}: { 
  onCreate: (name: string, date?: string) => void; 
  onClose: () => void;
}) {
  const theme = useSafeThemeClasses();
  const [meetingName, setMeetingName] = useState('');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);

  const handleCreate = () => {
    if (!meetingName.trim()) return;
    onCreate(meetingName.trim(), meetingDate);
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center p-4 z-50 ${theme.modalOverlay}`}>
      <div className={`${theme.card} rounded-xl shadow-xl max-w-md w-full p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Create New Meeting</h2>
          <button onClick={onClose} className={`p-2 rounded transition ${theme.buttonGhost}`}>
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Meeting Name</label>
            <input
              type="text"
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreate()}
              className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
              placeholder="Enter meeting name"
              autoFocus
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>Date</label>
            <input
              type="date"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonSecondary} ${theme.textSecondary}`}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className={`flex-1 px-4 py-2 rounded-lg transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
            >
              Create Meeting
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
